import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import { getPeriodError } from '../utils/billingPeriodValidation';
import { hasSellerTaxIdentification } from '../utils/sellerProfile';

// Mobile Components
import MobileXRechnungDashboard from '../components/invoice/mobile/MobileXRechnungDashboard';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import InvoiceMetaForm from '../components/invoice/InvoiceMetaForm';
import BuyerDetailsForm from '../components/invoice/BuyerDetailsForm';
import InvoiceItemsForm from '../components/invoice/InvoiceItemsForm';
import OptionalFieldsForm from '../components/invoice/OptionalFieldsForm';
import RelatedOfferSelector from '../components/invoice/RelatedOfferSelector';
import CollapsibleSection from '../components/ui/CollapsibleSection';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { InvoiceNumberPreview } from '../components/invoice/InvoiceNumberPreview';
import DownPaymentModal from '../components/modals/DownPaymentModal';
import { Seller, Buyer, ShipToAddress, InvoiceItem, InvoiceData } from '../types/invoiceTypes';
import { DownPayment } from '../types/downPaymentTypes';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { auth } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import logger, { nativeConsole } from '../utils/logger';
import { WorkflowStageType, WorkflowStageStatus, CreateWorkflowReferenceParams } from '../types/workflowTypes';
import { useInvoiceForm } from '../hooks/invoice/useInvoiceForm';
import { useInvoiceSubmission } from '../hooks/invoice/useInvoiceSubmission';
import { useCustomerManagement, CustomerManagementResult } from '../hooks/invoice/useCustomerManagement';
import { useProductManagement, ProductManagementResult as ProductManagementHookResult } from '../hooks/invoice/useProductManagement';
import { useInvoiceNumberPreview } from '../hooks/invoice/useInvoiceNumberPreview';
import InvoiceTypeSwitch from '../components/ui/InvoiceTypeSwitch';
import { INVOICE_FORM_ROUTE } from '../config/cockpitAccess';

// Draft functionality
import InvoiceDraftSelector from '../components/invoice/InvoiceDraftSelector';
import { 
    saveInvoiceDraft, 
    generateXrechnungPreview, 
    listInvoiceDrafts, 
    getInvoiceDraftDetails, 
    deleteInvoiceDraft 
} from '../api/invoiceDraftApi';
import { getAuth } from 'firebase/auth';
import { SingleValue } from 'react-select';
import { InvoiceUsageMeter } from '../components/usage/InvoiceUsageMeter';
import { useUsageLimitValueFraming } from '../hooks/invoice/useUsageLimitValueFraming';
import { FREEMIUM_INVOICE_LIMIT } from '../config/freemium';
import { buildUpgradeHref } from '../config/nudgePromotions';
import analyticsService from '../services/analyticsService';
import { formatCurrency, formatDate as formatDateCentral } from '../utils/formatters';
import { centsToEuros } from '../utils/moneyUtils';

// Diese Komponente ist eine angepasste Version von InvoiceDashboard für XRechnungen

const XRechnungDashboard: React.FC = () => {
    const { t, i18n } = useTranslation(['invoice', 'common']);
    const { isMobile } = useDeviceDetection();

    // State potentially remaining or needing specific management:
    const [hasRequiredSellerData, setHasRequiredSellerData] = useState<boolean>(true);
    const [searchParams] = useSearchParams();

    // Draft Management State
    const [availableDrafts, setAvailableDrafts] = useState<any[]>([]);
    const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null); // Aktuelle Entwurf-ID für Updates
    const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
    const [isLoadingDraftDetails, setIsLoadingDraftDetails] = useState(false);
    const [draftListError, setDraftListError] = useState<string | null>(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [draftSaveSuccess, setDraftSaveSuccess] = useState<string | null>(null); // Erfolgs-Nachricht für Entwurf-Speicherung
    const [token, setToken] = useState<string | null>(null);

    // Abschlagsrechnung State
    const [showDownPaymentModal, setShowDownPaymentModal] = useState<boolean>(false);

    // Auth Context
    const { currentUser, sellerData, sellerDataLoading, saveSellerData, refreshUserData, isPremium } = useAuth();
    // M11b-3: advisor (read-only) darf keine XRechnung erstellen/speichern.
    const isReadOnly = useIsReadOnly();
    
    // Custom Hooks Initialization
    const { 
        customerMeta, 
        checkForUpdate: checkCustomerUpdate, 
        handleCustomerSelection,
        dialogOpen: customerDialogOpen,
        setDialogOpen: setCustomerDialogOpen,
        dialogConfig: customerDialogConfig,
        handleDialogAction: handleCustomerDialogAction,
        releaseCustomerMetaLock,
        getCurrentCustomerMeta
    } = useCustomerManagement(); 

    const {
        productMetaForItems,
        checkAndHandleProductUpdate,
        handleProductSelection: handleProductSelectionForHook,
        resetProductManagement,
        dialogOpen: productDialogOpen,
        setDialogOpen: setProductDialogOpen,
        dialogConfig: productDialogConfig,
        handleDialogAction: handleProductDialogAction
    } = useProductManagement();

    const {
        // Form state values
        isAdvancedMode, invoiceDate, currency, buyer, items, deliveryDate, 
        useDifferentShipTo, shipToAddress, paymentTerms, notes, billingStartDate, 
        billingEndDate, tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
        contractReference, projectReferenceId, projectReferenceName, paymentDueDate, 
        paymentMandateReference, buyerOrderReference, sellerOrderReference, selectedOfferId, 
        selectedOfferNumber, relatedOfferData, offerWorkflowReferenceId,
        // Down payment state values
        existingDownPayments, hasDownPayments, totalDownPaymentAmount, remainingAmount, 
        isLoadingDownPayments, showDownPaymentSection,
        // Inline down payment creation state values
        inlineDownPaymentType, inlineDownPaymentValue, inlineDownPaymentDate, 
        inlineDownPaymentNotes, isCreatingInlineDownPayment,
        // Form state setters
        setIsAdvancedMode, setInvoiceDate, setCurrency, setBuyer, setItems, setDeliveryDate,
        setUseDifferentShipTo, setShipToAddress, setPaymentTerms, setNotes, setBillingStartDate,
        setBillingEndDate, setTenderOrLotReference, setInvoicedObjectId, setInvoicedObjectScheme,
        setContractReference, setProjectReferenceId, setProjectReferenceName, setPaymentDueDate,
        setPaymentMandateReference, setBuyerOrderReference, setSellerOrderReference, setSelectedOfferId,
        setSelectedOfferNumber, setRelatedOfferData, setOfferWorkflowReferenceId,
        // Down payment setters
        setExistingDownPayments, setHasDownPayments, setTotalDownPaymentAmount, setRemainingAmount,
        setIsLoadingDownPayments, setShowDownPaymentSection,
        // Inline down payment creation setters
        setInlineDownPaymentType, setInlineDownPaymentValue, setInlineDownPaymentDate,
        setInlineDownPaymentNotes, setIsCreatingInlineDownPayment,
        resetForm: resetInvoiceForm,
        handleOfferSelection,
        handleInlineDownPaymentCreation,
        offerSelectionError
    } = useInvoiceForm();

    // Re-Loop-Auflage 🟡-2 (review-report-leistungszeitraum-mobil.md): derselbe
    // Fehlerzustand wie in OptionalFieldsForm, hier gebraucht, um ihn zusätzlich
    // am (auch zugeklappten) Abschnittskopf sichtbar zu machen.
    const billingPeriodError = useMemo(
        () => getPeriodError(billingStartDate, billingEndDate),
        [billingStartDate, billingEndDate]
    );

    // Custom hook für XRechnung-Submission
    const {
        submitInvoice: submitXRechnung,
        isLoading: isSubmitting,
        error: submissionError,
        errorType: submissionErrorType,
        setError: setSubmissionError
    } = useInvoiceSubmission('xrechnung'); // Übergebe 'xrechnung' als Typ

    // Rechnungsnummer-Vorschau Hook
    const {
        xrechnungPreview,
        isLoading: isLoadingPreview
    } = useInvoiceNumberPreview();

    const navigate = useNavigate();

    // Effects
    useEffect(() => {
        if (!sellerDataLoading) {
            // XRechnung requires email and phone in addition to address
            const hasData = !!(
                sellerData && 
                sellerData.name && 
                sellerData.street && 
                sellerData.zip && 
                sellerData.city && 
                sellerData.country &&
                (sellerData.email || sellerData.contact?.email) &&
                (sellerData.phone || sellerData.contact?.phone)
            );
            // § 14 Abs. 4 Nr. 2 UStG: Steuernummer oder USt-IdNr. für ALLE Verkäufer (#339)
            setHasRequiredSellerData(hasData && hasSellerTaxIdentification(sellerData));
        }
    }, [sellerData, sellerDataLoading]);

    useEffect(() => {
        const hasShipToData = !!(shipToAddress.name || shipToAddress.street || shipToAddress.street2 || shipToAddress.street3 || (shipToAddress.zip && shipToAddress.zip !== '') || (shipToAddress.city && shipToAddress.city !== '') || (shipToAddress.country && shipToAddress.country !== 'DE'));
        if (hasShipToData !== useDifferentShipTo) {
            setUseDifferentShipTo(hasShipToData);
        }
    }, [shipToAddress, useDifferentShipTo, setUseDifferentShipTo]);

    // Token Management
    useEffect(() => {
        const getToken = async () => {
            if (currentUser) {
                try {
                    const authToken = await getAuth().currentUser?.getIdToken(true);
                    setToken(authToken || null);
                } catch (error) {
                    console.error("Fehler beim Laden des Auth-Tokens:", error);
                    setToken(null);
                }
            } else {
                setToken(null);
            }
        };
        getToken();
    }, [currentUser]);

    // Load Drafts
    useEffect(() => {
        const loadDrafts = async () => {
            if (!token) return;
            
            setIsLoadingDrafts(true);
            setDraftListError(null);
            
            try {
                const drafts = await listInvoiceDrafts(token);
                // Filter only XRechnung drafts for this dashboard
                const xrechnungDrafts = drafts.filter(draft => draft.type === 'xrechnung');
                setAvailableDrafts(xrechnungDrafts);
                console.log('XRechnung-Draft-Liste geladen:', xrechnungDrafts);
            } catch (error: any) {
                console.error('Fehler beim Laden der XRechnung-Drafts:', error);
                setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.loadFailed' }));
            } finally {
                setIsLoadingDrafts(false);
            }
        };

        loadDrafts();
    }, [token, t]);

    // Event-Listener für Draft-Löschung nach Rechnungserstellung
    useEffect(() => {
        const handleDraftDeleted = (event: CustomEvent) => {
            const { deletedDraftId } = event.detail;
            
            // Entferne den gelöschten Draft aus der lokalen Liste
            setAvailableDrafts(prevDrafts => 
                prevDrafts.filter(draft => draft.id !== deletedDraftId)
            );
            
            // Resetiere die Auswahl, falls der gelöschte Draft ausgewählt war
            if (selectedDraftId === deletedDraftId) {
                setSelectedDraftId(null);
            }
            
            logger.info(`Draft-Liste aktualisiert nach Löschung von Draft: ${deletedDraftId}`);
        };

        // Event-Listener hinzufügen
        window.addEventListener('draftDeleted', handleDraftDeleted as EventListener);

        // Cleanup beim Unmount
        return () => {
            window.removeEventListener('draftDeleted', handleDraftDeleted as EventListener);
        };
    }, [selectedDraftId]);

    // Callback Handlers
    const handleDownPaymentClick = () => {
        // Berechne den Gesamtbetrag korrekt mit Steuern
        const totalAmount = items.reduce((sum, item) => {
            const netPrice = item.netPrice * item.quantity;
            const taxAmount = netPrice * (item.taxRate / 100);
            return sum + netPrice + taxAmount;
        }, 0);

        setExistingDownPayments([]); // TODO: Lade existierende Abschlagsrechnungen
        setShowDownPaymentModal(true);
    };

    const closeDownPaymentModal = () => {
        setShowDownPaymentModal(false);
    };

    const handleDownPaymentCreated = async (newDownPayment: DownPayment) => {
        try {
            // Füge die neue Abschlagsrechnung zu den existierenden hinzu
            const updatedDownPayments = [...existingDownPayments, newDownPayment];
            const newTotalDownPaymentAmount = updatedDownPayments.reduce((sum, dp) => sum + dp.amount, 0);
            
            // Update all related states
            setExistingDownPayments(updatedDownPayments);
            setHasDownPayments(true);
            setTotalDownPaymentAmount(newTotalDownPaymentAmount);
            
            // Calculate remaining amount based on current items
            const currentTotal = items.reduce((sum, item) => {
                const netPrice = item.netPrice * item.quantity;
                const taxAmount = netPrice * (item.taxRate / 100);
                return sum + netPrice + taxAmount;
            }, 0);
            setRemainingAmount(Math.max(0, currentTotal - newTotalDownPaymentAmount));
            
            // Schließe das Modal
            closeDownPaymentModal();
            
            // Zeige Erfolgs-Nachricht (optional)
            console.log('Abschlagsrechnung erfolgreich erstellt:', newDownPayment);
            
        } catch (error) {
            console.error('Fehler beim Verarbeiten der erstellten Abschlagsrechnung:', error);
        }
    };

    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            logger.error(error, "logout error");
        }
    }, [navigate]);

    const resetAllForms = useCallback(() => {
        resetInvoiceForm();
        resetProductManagement();
        setCurrentDraftId(null); // Reset der aktuellen Draft-ID bei Formular-Reset
        setSelectedDraftId(null); // Reset auch der ausgewählten Draft-ID
        nativeConsole.log("All relevant form states reset.");
    }, [resetInvoiceForm, resetProductManagement]);

    const closeAllDialogs = useCallback(() => {
        setCustomerDialogOpen(false);
        setProductDialogOpen(false);
    }, [setCustomerDialogOpen, setProductDialogOpen]);

    // Handler für XRechnung-Abschlussrechnung
    const handleFinalInvoiceSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!currentUser || !token) {
            setSubmissionError('Authentifizierung erforderlich');
            return;
        }

        try {
            const customerResult = await checkCustomerUpdate(buyer);
            if (customerResult === false) {
                return;
            }

            const productResult = await checkAndHandleProductUpdate(items);
            if (productResult === false) {
                return;
            }

            // Importiere die Final Invoice API und Tax Utils
            const { createFinalInvoiceFromDownPayments } = await import('../api/downPaymentApi');
            const { calculateFinalInvoiceWithTaxes } = await import('../utils/taxCalculationUtils');
            
            // Berechne korrekte Steuerverteilung
            const taxCalculation = calculateFinalInvoiceWithTaxes(items, existingDownPayments);
            
            // Erstelle Final Invoice Request für XRechnung
            const finalInvoiceRequest = {
                baseDocumentId: selectedOfferId || '',
                baseDocumentType: 'offer' as const,
                baseDocumentNumber: selectedOfferNumber || undefined, // Benutzerfreundliche Angebotsnummer
                invoiceDate: invoiceDate,
                notes: notes || undefined,
                includeDownPaymentDetails: true,
                format: 'xrechnung' as const,
                workflowReferenceId: offerWorkflowReferenceId || undefined,
                remainingAmount: remainingAmount,
                existingDownPayments: existingDownPayments,
                // Vollständige Dokumentdaten
                seller: sellerData || undefined,
                buyer: buyer,
                items: items,
                deliveryDate: deliveryDate || undefined,
                paymentTerms: paymentTerms || undefined,
                billingStartDate: billingStartDate || undefined,
                billingEndDate: billingEndDate || undefined,
                contractReference: contractReference || undefined,
                buyerOrderReference: buyerOrderReference || undefined,
                sellerOrderReference: sellerOrderReference || undefined,
                relatedOfferId: selectedOfferId || undefined,
                deliveryAddress: useDifferentShipTo ? shipToAddress : undefined
            };

            // Erstelle die XRechnung-Abschlussrechnung
            const result = await createFinalInvoiceFromDownPayments(finalInvoiceRequest, token);
            
            // Download der XML-Datei
            const { downloadXRechnungBlob } = await import('../api/xRechnungApi');
            downloadXRechnungBlob(result.blob, result.filename);
            
            // Formular zurücksetzen
            resetAllForms();
            
            // **FIX: Release customer meta lock after successful final invoice submission**
            releaseCustomerMetaLock();
            
            // Erfolgsmeldung entfernt

        } catch (error) {
            logger.error('Fehler bei der XRechnung-Abschlussrechnungserstellung:', error);
            setSubmissionError((error as Error).message || 'Unbekannter Fehler bei der XRechnung-Abschlussrechnungserstellung');
        }
    }, [
        currentUser, token, buyer, items, checkCustomerUpdate, checkAndHandleProductUpdate,
        selectedOfferId, invoiceDate, notes, offerWorkflowReferenceId, remainingAmount,
        existingDownPayments, sellerData, deliveryDate, paymentTerms, billingStartDate,
        billingEndDate, contractReference, buyerOrderReference, sellerOrderReference,
        useDifferentShipTo, shipToAddress, resetAllForms, setSubmissionError
    ]);

    // Handle form submit
    const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            // Prüfe zuerst Kunden- und Produktdialoge (wie im Original)
            const customerResult = await checkCustomerUpdate(buyer);
            if (customerResult === false) { // Validierung fehlgeschlagen
                return;
            }

            const productResult = await checkAndHandleProductUpdate(items);
            if (productResult === false) { // Validierung fehlgeschlagen
                return;
            }
            
            // XRechnung einreichen
            nativeConsole.log('Starting XRechnung submission...');
            // **FIX: Verwende getCurrentCustomerMeta für aktuellste Daten**
            const currentCustomerMeta = getCurrentCustomerMeta();
            nativeConsole.log('CustomerMeta passed to submitXRechnung:', currentCustomerMeta);
            nativeConsole.log('CustomerMeta action:', currentCustomerMeta.action);
            nativeConsole.log('CustomerMeta customerId:', currentCustomerMeta.customerId);
            await submitXRechnung(
                {
                    invoiceDate, currency, buyer, 
                    deliveryDate, useDifferentShipTo, shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                    paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
                    invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
                    projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
                    sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
                    
                                            customerMeta: getCurrentCustomerMeta(),
                        
                        items: items.map(item => ({
                        ...item,
                        itemAction: productMetaForItems[item.id]?.action || 'none',
                        productId: productMetaForItems[item.id]?.productId || null,
                    })),
                    productAction: Object.values(productMetaForItems).some(meta => meta.action === 'create' || meta.action === 'update') 
                                ? 'update' 
                                : 'none',
                    selectedDraftId,
                },
                resetAllForms
            );
            // **FIX: Release customer meta lock after successful XRechnung submission**
            releaseCustomerMetaLock();
        } catch (error) {
            nativeConsole.error('Fehler bei der XRechnung-Erstellung:', error);
            setSubmissionError((error as Error).message || 'Unbekannter Fehler bei der XRechnung-Erstellung');
        }
    }, [
        buyer, 
        items,
        customerDialogOpen,
        productDialogOpen, 
        checkCustomerUpdate,
        checkAndHandleProductUpdate,
        submitXRechnung,
        invoiceDate, 
        currency, 
        deliveryDate, 
        useDifferentShipTo, 
        shipToAddress,
        paymentTerms, 
        notes, 
        billingStartDate, 
        billingEndDate, 
        tenderOrLotReference,
        invoicedObjectId, 
        invoicedObjectScheme, 
        contractReference, 
        projectReferenceId,
        projectReferenceName, 
        paymentDueDate, 
        paymentMandateReference, 
        buyerOrderReference,
        sellerOrderReference, 
        selectedOfferId, 
        offerWorkflowReferenceId,
        customerMeta,
        productMetaForItems,
        resetAllForms,
        selectedDraftId
    ]);

    // Customer Dialog Handler
    const onCustomerDialogAction = useCallback(async (action: string) => {
        try {
            const result = await handleCustomerDialogAction(action);
            logger.debug(`Kundendialog wurde mit Ergebnis geschlossen: ${result}`);
            
            if (result === 'continue') {
                const productResult = await checkAndHandleProductUpdate(items);
                if (productResult === false || productResult === 'continue') { // Kein Dialog oder continue
                    await submitXRechnung(
                        {
                            invoiceDate, currency, buyer, 
                            deliveryDate, useDifferentShipTo, shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                            paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
                            invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
                            projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
                            sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
                            
                            customerMeta: getCurrentCustomerMeta(), 
                            
                            items: items.map(item => ({
                                ...item,
                                itemAction: productMetaForItems[item.id]?.action || 'none',
                                productId: productMetaForItems[item.id]?.productId || null,
                            })),
                            productAction: Object.values(productMetaForItems).some(meta => meta.action === 'create' || meta.action === 'update') 
                                        ? 'update' 
                                        : 'none',
                            selectedDraftId,
                        },
                        resetAllForms
                    );
                    // **FIX: Release customer meta lock after successful XRechnung submission**
                    releaseCustomerMetaLock();
                }
            }
        } catch (error) {
            setSubmissionError(`Fehler beim Kundendialog: ${(error as Error).message}`);
            nativeConsole.error("Fehler beim Kundendialog:", error);
        }
    }, [
        handleCustomerDialogAction, items, checkAndHandleProductUpdate, submitXRechnung,
        invoiceDate, currency, buyer, deliveryDate, useDifferentShipTo, shipToAddress,
        paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
        invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
        projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
        sellerOrderReference, selectedOfferId, offerWorkflowReferenceId, customerMeta,
        productMetaForItems, resetAllForms, setSubmissionError, selectedDraftId, getCurrentCustomerMeta
    ]);

    // Product Dialog Handler
    const onProductDialogAction = useCallback(async (action: string) => {
        try {
            const result = await handleProductDialogAction(action);
            logger.debug(`Produktdialog wurde mit Ergebnis geschlossen: ${result}`);
            
            if (result === 'continue') {
                await submitXRechnung(
                    {
                        invoiceDate, currency, buyer, 
                        deliveryDate, useDifferentShipTo, shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                        paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
                        invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
                        projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
                        sellerOrderReference, selectedOfferId, offerWorkflowReferenceId,
                        
                        customerMeta: getCurrentCustomerMeta(), 
                        
                        items: items.map(item => ({
                            ...item,
                            itemAction: productMetaForItems[item.id]?.action || 'none',
                            productId: productMetaForItems[item.id]?.productId || null,
                        })),
                        productAction: Object.values(productMetaForItems).some(meta => meta.action === 'create' || meta.action === 'update') 
                                    ? 'update' 
                                    : 'none',
                        selectedDraftId,
                    },
                    resetAllForms
                );
                // **FIX: Release customer meta lock after successful XRechnung submission**
                releaseCustomerMetaLock();
            }
        } catch (error) {
            setSubmissionError(`Fehler beim Produktdialog: ${(error as Error).message}`);
            nativeConsole.error("Fehler beim Produktdialog:", error);
        }
    }, [
        handleProductDialogAction, submitXRechnung, 
        invoiceDate, currency, buyer, items, deliveryDate, useDifferentShipTo, shipToAddress,
        paymentTerms, notes, billingStartDate, billingEndDate, tenderOrLotReference,
        invoicedObjectId, invoicedObjectScheme, contractReference, projectReferenceId,
        projectReferenceName, paymentDueDate, paymentMandateReference, buyerOrderReference,
        sellerOrderReference, selectedOfferId, offerWorkflowReferenceId, customerMeta,
        productMetaForItems, resetAllForms, setSubmissionError, selectedDraftId, getCurrentCustomerMeta
    ]);

    // Draft Management Handlers
    const handleSaveDraft = async () => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Draft-Speicherung');
            return;
        }

        setIsSavingDraft(true);

        try {
            const invoiceData = {
                invoiceDate, currency, buyer, items, deliveryDate, 
                useDifferentShipTo, 
                shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                paymentTerms, notes, billingStartDate, billingEndDate, 
                tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
                contractReference, projectReferenceId, projectReferenceName, 
                paymentDueDate, paymentMandateReference, buyerOrderReference,
                sellerOrderReference,
                seller: sellerData!,
                type: 'xrechnung',
                // NEU: Angebots- und Workflow-Informationen für Draft speichern
                relatedOfferIds: selectedOfferId ? [selectedOfferId] : undefined,
                workflowReferenceId: offerWorkflowReferenceId || undefined
            };

            let draftId: string;

            // Prüfen ob bereits ein Entwurf existiert (currentDraftId oder selectedDraftId)
            const existingDraftId = currentDraftId || selectedDraftId;
            
            if (existingDraftId) {
                // Bestehenden Entwurf löschen, bevor neuer erstellt wird
                console.log('Lösche bestehenden XRechnung-Draft vor Neuerstellung:', existingDraftId);
                try {
                    await deleteInvoiceDraft(existingDraftId, token);
                    console.log('Alter Draft erfolgreich gelöscht, erstelle jetzt neuen');
                } catch (deleteError) {
                    console.warn('Draft-Löschung fehlgeschlagen, erstelle trotzdem neuen Draft:', deleteError);
                }
            }
            
            // Neuen Entwurf erstellen
            console.log('Erstelle neuen XRechnung-Draft');
            const result = await saveInvoiceDraft(invoiceData, token);
            draftId = result.draftId;
            setCurrentDraftId(draftId); // Neue Draft-ID für zukünftige Updates merken
            setSelectedDraftId(draftId); // Auch als ausgewählten Entwurf setzen für UI-Anzeige
            console.log('XRechnung-Draft erfolgreich erstellt:', draftId);

            // Draft-Liste neu laden
            const drafts = await listInvoiceDrafts(token);
            const xrechnungDrafts = drafts.filter(draft => draft.type === 'xrechnung');
            setAvailableDrafts(xrechnungDrafts);

            // Erfolgs-Nachricht anzeigen
            setDraftSaveSuccess('Entwurf erfolgreich gespeichert!');
            // Nach 3 Sekunden wieder ausblenden
            setTimeout(() => setDraftSaveSuccess(null), 3000);
        } catch (error: any) {
            console.error('Fehler beim Speichern des XRechnung-Drafts:', error);
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.saveFailed' }));
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleGeneratePreview = async () => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Preview-Generierung');
            return;
        }

        setIsGeneratingPreview(true);

        try {
            const invoiceData = {
                invoiceDate, currency, buyer, items, deliveryDate, 
                useDifferentShipTo, 
                shipToAddress: useDifferentShipTo ? shipToAddress : null, 
                paymentTerms, notes, billingStartDate, billingEndDate, 
                tenderOrLotReference, invoicedObjectId, invoicedObjectScheme, 
                contractReference, projectReferenceId, projectReferenceName, 
                paymentDueDate, paymentMandateReference, buyerOrderReference,
                sellerOrderReference,
                seller: sellerData!,
                type: 'xrechnung'
            };

            let xmlBlob: Blob;
            let draftId: string;

            // Prüfen ob bereits ein Entwurf existiert (currentDraftId oder selectedDraftId)
            const existingDraftId = currentDraftId || selectedDraftId;
            
            if (existingDraftId) {
                // Bestehenden Entwurf löschen, bevor neue Vorschau generiert wird
                console.log('Lösche bestehenden Draft vor neuer Vorschau-Generierung:', existingDraftId);
                try {
                    await deleteInvoiceDraft(existingDraftId, token);
                    console.log('Alter Draft erfolgreich gelöscht, generiere jetzt neue Vorschau');
                } catch (deleteError) {
                    console.warn('Draft-Löschung fehlgeschlagen, fahre mit normaler Vorschau fort:', deleteError);
                }
            }
            
            // Neue Vorschau generieren (erstellt neuen Entwurf)
            console.log('Generiere neue XRechnung-Preview');
            const result = await generateXrechnungPreview(invoiceData, token);
            xmlBlob = result.xmlBlob;
            draftId = result.draftId;
            setCurrentDraftId(draftId); // Neue Draft-ID für zukünftige Updates merken
            setSelectedDraftId(draftId); // Auch als ausgewählten Entwurf setzen für UI-Anzeige
            console.log('XRechnung-Preview erfolgreich generiert:', draftId);

            // XML herunterladen
            const url = window.URL.createObjectURL(xmlBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `XRechnung-Vorschau-${new Date().toISOString().split('T')[0]}.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Draft-Liste neu laden
            const drafts = await listInvoiceDrafts(token);
            const xrechnungDrafts = drafts.filter(draft => draft.type === 'xrechnung');
            setAvailableDrafts(xrechnungDrafts);
        } catch (error: any) {
            console.error('Fehler beim Generieren der XRechnung-Vorschau:', error);
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.invoice.previewFailed' }));
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    const handleSelectDraft = async (selectedOption: SingleValue<any>) => {
        if (!selectedOption || !token) {
            setSelectedDraftId(null);
            setCurrentDraftId(null); // Reset auch die aktuelle Draft-ID
            return;
        }

        setSelectedDraftId(selectedOption.value);
        setCurrentDraftId(selectedOption.value); // Setze die ausgewählte Draft-ID als aktuelle für Updates
        setIsLoadingDraftDetails(true);

        try {
            const draftDetails = await getInvoiceDraftDetails(selectedOption.value, token);
            console.log('Geladene XRechnung-Draft-Details:', draftDetails);

            // Formular mit Draft-Daten befüllen
            const rawData = draftDetails.rawData;
            if (rawData) {
                // Setze alle Formularfelder mit den gespeicherten Daten
                setInvoiceDate(rawData.invoiceDate || new Date().toISOString().split('T')[0]);
                setCurrency(rawData.currency || 'EUR');
                setBuyer(rawData.buyer || { name: '', street: '', zip: '', city: '', country: 'DE' });
                setItems(rawData.items || []);
                setDeliveryDate(rawData.deliveryDate || '');
                setUseDifferentShipTo(rawData.useDifferentShipTo || false);
                setShipToAddress(rawData.shipToAddress || { name: '', street: '', zip: '', city: '', country: 'DE' });
                setPaymentTerms(rawData.paymentTerms || '');
                setNotes(rawData.notes || '');
                setBillingStartDate(rawData.billingStartDate || '');
                setBillingEndDate(rawData.billingEndDate || '');
                setTenderOrLotReference(rawData.tenderOrLotReference || '');
                setInvoicedObjectId(rawData.invoicedObjectId || '');
                setInvoicedObjectScheme(rawData.invoicedObjectScheme || '');
                setContractReference(rawData.contractReference || '');
                setProjectReferenceId(rawData.projectReferenceId || '');
                setProjectReferenceName(rawData.projectReferenceName || '');
                setPaymentDueDate(rawData.paymentDueDate || '');
                setPaymentMandateReference(rawData.paymentMandateReference || '');
                setBuyerOrderReference(rawData.buyerOrderReference || '');
                setSellerOrderReference(rawData.sellerOrderReference || '');
                
                // NEU: Angebotsinformationen wiederherstellen
                if (draftDetails.relatedOfferId) {
                    setSelectedOfferId(draftDetails.relatedOfferId);
                    console.log('Wiederhergestellte relatedOfferId:', draftDetails.relatedOfferId);
                }
                if (draftDetails.workflowReferenceId) {
                    setOfferWorkflowReferenceId(draftDetails.workflowReferenceId);
                    console.log('Wiederhergestellte workflowReferenceId:', draftDetails.workflowReferenceId);
                }
                // Versuche Angebotsnummer zu laden falls verfügbar
                if (rawData.selectedOfferId || draftDetails.relatedOfferId) {
                    const offerId = rawData.selectedOfferId || draftDetails.relatedOfferId;
                    // Hier könnte man optional die Angebotsnummer laden
                    console.log('Draft basiert auf Angebot:', offerId);
                }
            }
        } catch (error: any) {
            console.error('Fehler beim Laden der XRechnung-Draft-Details:', error);
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.loadFailed' }));
            setSelectedDraftId(null);
        } finally {
            setIsLoadingDraftDetails(false);
        }
    };

    const handleDeleteDraft = async (draftId: string) => {
        if (!token) {
            console.error('Kein Auth-Token verfügbar für Draft-Löschung');
            return;
        }

        // Optimistic Update: Sofort aus der Liste entfernen
        const originalDrafts = [...availableDrafts];
        const filteredDrafts = availableDrafts.filter(draft => draft.id !== draftId);
        setAvailableDrafts(filteredDrafts);

        // Wenn der gelöschte Draft der aktuell ausgewählte war, Auswahl zurücksetzen
        if (selectedDraftId === draftId) {
            setSelectedDraftId(null);
        }

        try {
            // Backend-Löschung im Hintergrund
            await deleteInvoiceDraft(draftId, token);
            console.log('XRechnung-Draft erfolgreich gelöscht:', draftId);
        } catch (error: any) {
            console.error('Fehler beim Löschen des XRechnung-Drafts:', error);
            
            // Bei Fehler: Original-Liste wiederherstellen
            setAvailableDrafts(originalDrafts);
            if (selectedDraftId === draftId) {
                setSelectedDraftId(draftId); // Auswahl wiederherstellen
            }
            
            setDraftListError(mapErrorToUserMessage(error, t, { fallbackKey: 'errors.draft.deleteFailed' }));
        }
    };

    // Combine all potential errors for display
    const showUsageLimitPromo = submissionErrorType === 'usageLimit';
    const displayError = showUsageLimitPromo ? offerSelectionError : (submissionError || offerSelectionError);

    // M-UX-G4: Value-Framing für den 403-Limit-Moment (fetch-on-demand, s. Hook-JSDoc).
    const { revenueCents: usageLimitRevenueCents, resolved: usageLimitRevenueResolved } = useUsageLimitValueFraming(
        currentUser?.uid,
        showUsageLimitPromo,
    );

    // M-N3 (architecture-nudge.md §7.4): view_promotion GENAU 1x pro Banner-
    // Erscheinen (steigende Flanke von showUsageLimitPromo), unabhängig davon,
    // wie oft der Effekt wegen der async Umsatz-Auflösung erneut läuft — dafür
    // ein Ref-Latch statt eines reinen Dep-Arrays. Wir warten bewusst auf
    // `usageLimitRevenueResolved`, bevor wir feuern, statt sofort mit einem
    // Zwischenstand zu feuern: sonst würde für umsatztragende Nutzer zuerst
    // 'generic' und nach Auflösung des Fetches erneut 'value_framed' feuern
    // (Doppel-Fire, F-1 Review-Finding) — creative_slot kodiert value_framed
    // (PromotionEventData/M-N1 hat kein eigenes Boolean-Feld dafür).
    const usageLimitBannerFiredRef = useRef(false);
    useEffect(() => {
        if (!showUsageLimitPromo) {
            usageLimitBannerFiredRef.current = false;
            return;
        }
        if (usageLimitBannerFiredRef.current || !usageLimitRevenueResolved) return;
        usageLimitBannerFiredRef.current = true;
        analyticsService.trackViewPromotion({
            promotion_id: 'usage_limit_banner',
            creative_slot: usageLimitRevenueCents !== null ? 'value_framed' : 'generic',
            limit_value: FREEMIUM_INVOICE_LIMIT,
        });
    }, [showUsageLimitPromo, usageLimitRevenueResolved, usageLimitRevenueCents]);

    // Render mobile version if on mobile device
    if (isMobile) {
        return <MobileXRechnungDashboard />;
    }

    // --- Render Logic --- 
    return (
        <div className="container mx-auto p-4 bg-surface min-h-screen text-primary">
            <h1 className="text-3xl font-bold mb-6 text-accent-ink">{t('xrechnung.title')}</h1>

            {/* NEUER SWITCH HIER */}
            <InvoiceTypeSwitch currentType="xrechnung" basePath="/dashboard" zugferdPath={INVOICE_FORM_ROUTE} />

            {/* Info-Box über XRechnung */}
            <div className="p-4 mb-6 bg-[var(--tone-info-bg)] border border-blue-500 rounded-lg">
                <div className="flex items-start">
                    <svg className="w-5 h-5 text-[var(--tone-info-fg)] mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div>
                        <h3 className="text-sm font-medium text-[var(--tone-info-fg)]">{t('xrechnung.format')}</h3>
                        <p className="text-sm text-[var(--tone-info-fg)] mt-1">
                            {t('xrechnung.description')}
                        </p>
                    </div>
                </div>
            </div>

            {/* M-UX-G4: Free-Tier-Verbrauchszähler — VOR dem Limit-Moment sichtbar, kein Zähler für Premium.
                Gate auf !sellerDataLoading: verhindert einen "0 von 4"-Flash für Premium-Nutzer, solange
                isPremium noch seinen Default (false) trägt, während sellerData lädt. */}
            {!sellerDataLoading && (
                <InvoiceUsageMeter count={sellerData?.monthlyInvoiceCount ?? 0} isPremium={isPremium} />
            )}

            {/* Draft Management Section */}
            {hasRequiredSellerData && (
                <InvoiceDraftSelector
                    drafts={availableDrafts}
                    selectedDraftId={selectedDraftId}
                    onSelectDraft={handleSelectDraft}
                    onDeleteDraft={handleDeleteDraft}
                    isLoading={isLoadingDrafts}
                    error={draftListError}
                    title={t('xrechnung.useDraftTitle')}
                    filterType="xrechnung"
                />
            )}

            {/* Seller Data Check */} 
            {!sellerDataLoading && !hasRequiredSellerData && (
                <div className="p-6 mb-6 bg-[var(--tone-accent-bg)] border border-indigo-500 rounded-lg">
                    <div className="flex flex-col items-center text-center">
                        <h3 className="text-xl font-semibold text-primary mb-3">Verkäuferdaten vervollständigen</h3>
                        <p className="text-secondary mb-4">
                            {!hasSellerTaxIdentification(sellerData)
                                ? t('common:sellerDataIncomplete.taxIdMissing')
                                : <>{t('xrechnung.sellerDataRequired')} 
                            Diese Pflichtangaben erscheinen auf allen Ihren Rechnungen und sind gesetzlich erforderlich.</>}
                        </p>
                        <Link 
                            to="/dashboard/konto" 
                            className="px-5 py-3 text-[#0C1322] bg-indigo-600 hover:bg-indigo-700 rounded-md font-medium"
                        >
                            Jetzt Daten eingeben
                        </Link>
                    </div>
                </div>
            )}

            {showUsageLimitPromo && (
                <div className="mb-4 p-5 bg-[var(--tone-accent-bg)] border border-indigo-600 rounded-lg" role="status">
                    <div className="flex items-start">
                        <svg className="w-6 h-6 text-accent-ink mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M12 3a9 9 0 100 18 9 9 0 000-18z" />
                        </svg>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-[var(--tone-accent-fg)]">
                                {t('common:usageLimitPromo.title', { limit: FREEMIUM_INVOICE_LIMIT })}
                            </h3>
                            <p className="text-sm text-[var(--tone-accent-fg)] mt-1">
                                {usageLimitRevenueCents !== null
                                    ? t('common:usageLimitPromo.valueMessage', {
                                          amount: formatCurrency(centsToEuros(usageLimitRevenueCents)),
                                      })
                                    : t('common:usageLimitPromo.message')}
                            </p>
                            <p className="text-xs text-[var(--tone-accent-fg)] mt-1">
                                {t('common:usageLimitPromo.yearlyAnchor')}
                            </p>
                            <div className="mt-3 flex flex-wrap items-center gap-3">
                                <Link
                                    to={buildUpgradeHref('usage_limit_banner')}
                                    onClick={() => analyticsService.trackSelectPromotion({
                                        promotion_id: 'usage_limit_banner',
                                        creative_slot: usageLimitRevenueCents !== null ? 'value_framed' : 'generic',
                                        limit_value: FREEMIUM_INVOICE_LIMIT,
                                    })}
                                    className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-[#0C1322] text-sm font-semibold transition-colors"
                                >
                                    {t('common:usageLimitPromo.cta')}
                                </Link>
                                <span className="text-xs text-[var(--tone-accent-fg)]">
                                    {t('common:usageLimitPromo.note')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Display */} 
            {displayError && (
                <div className="mb-4 p-4 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] rounded" role="alert">
                    <strong className="font-bold">Fehler:</strong>
                    <span className="block sm:inline"> {displayError}</span>
                </div>
            )}

            {/* Main Form */} 
            {hasRequiredSellerData && (
                <div className="grid grid-cols-1 gap-6">
                    <div className="bg-surface-raised shadow-lg rounded-lg p-6 border border-hairline space-y-6">
                        <form onSubmit={hasDownPayments ? handleFinalInvoiceSubmit : handleFormSubmit}>

                            {/* Offer Selector */} 
                            <div className="mb-6 p-4 border border-hairline rounded-md shadow-sm bg-surface-inset">
                                <h2 className="text-lg font-medium mb-4 text-secondary">{t('offer.useAsBase')}</h2>
                                <RelatedOfferSelector 
                                    onSelectOffer={handleOfferSelection} 
                                    selectedOfferId={selectedOfferId} 
                                />
                                {selectedOfferId && (
                                    <div className="mt-2 text-sm text-muted">
                                        {t('xrechnung.basedOnOffer')}: <span className="font-medium text-primary">{selectedOfferNumber}</span>
                                    </div>
                                )}
                            </div>

                                                        {/* Down Payment Section - Show when offer with workflow is selected */}
                            {offerWorkflowReferenceId && (
                                <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                    <div className="flex items-center justify-between mb-4 border-b border-strong pb-2">
                                        <h2 className="text-xl font-semibold text-primary">{t('downPayment.title')}</h2>
                                        <button
                                            type="button"
                                            onClick={() => setShowDownPaymentSection(!showDownPaymentSection)}
                                            className="text-accent-ink hover:text-accent-ink text-sm font-medium"
                                        >
                                            {showDownPaymentSection ? t('common:hide') : t('common:show')}
                                        </button>
                                    </div>
                                    
                                    {isLoadingDownPayments ? (
                                        <div className="text-center py-4">
                                            <div className="inline-flex items-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="text-secondary">Lade Abschlagsrechnungen...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="bg-surface-inset p-4 rounded-md">
                                                <div className="grid grid-cols-3 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-muted">Bereits bezahlt:</span>
                                                        <div className="text-[var(--tone-success-fg)] font-semibold">
                                                            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency }).format(totalDownPaymentAmount)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted">Noch offen:</span>
                                                        <div className="text-[var(--tone-warning-fg)] font-semibold">
                                                            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency }).format(remainingAmount)}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted">Anzahl Abschläge:</span>
                                                        <div className="text-[var(--tone-info-fg)] font-semibold">
                                                            {existingDownPayments.length}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {showDownPaymentSection && selectedOfferId && (
                                                <div className="space-y-2">
                                                    {existingDownPayments.length > 0 && (
                                                        <>
                                                            <h4 className="text-sm font-medium text-secondary mb-2">Vorhandene Abschlagsrechnungen:</h4>
                                                            {existingDownPayments.map((dp, index) => (
                                                                <div key={dp.id} className="bg-surface-hover p-3 rounded text-sm">
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-secondary">
                                                                            {dp.invoiceNumber} ({formatDateCentral(dp.invoiceDate, { locale: i18n.language })})
                                                                        </span>
                                                                        <span className="text-[var(--tone-success-fg)] font-medium">
                                                                            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: dp.currency }).format(dp.amount)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                    
                                                    {/* Inline-Erstellungsbereich für neue Abschlagsrechnung
                                                        M11b-3: für advisor (read-only) ausgeblendet */}
                                                    {!isReadOnly && (
                                                    <div className="bg-surface-inset p-4 rounded border border-strong">
                                                        <h5 className="text-sm font-medium text-secondary mb-3">Neue Abschlagsrechnung erstellen</h5>
                                                        <div className="space-y-3">
                                                            <div className="flex space-x-3">
                                                                <div className="flex-1">
                                                                    <label className="block text-xs text-muted mb-1">Typ</label>
                                                                    <select 
                                                                        className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                        value={inlineDownPaymentType}
                                                                        onChange={(e) => setInlineDownPaymentType(e.target.value as 'percentage' | 'amount')}
                                                                    >
                                                                        <option value="percentage">Prozentsatz</option>
                                                                        <option value="amount">Betrag</option>
                                                                    </select>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <label className="block text-xs text-muted mb-1">
                                                                        Wert {inlineDownPaymentType === 'percentage' ? '(%)' : '(€)'}
                                                                    </label>
                                                                    <input 
                                                                        type="number" 
                                                                        className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                        placeholder={inlineDownPaymentType === 'percentage' ? '50' : '1000'}
                                                                        value={inlineDownPaymentValue}
                                                                        onChange={(e) => setInlineDownPaymentValue(e.target.value)}
                                                                        min="0"
                                                                        step={inlineDownPaymentType === 'percentage' ? '0.01' : '0.01'}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs text-muted mb-1">Rechnungsdatum</label>
                                                                <input 
                                                                    type="date" 
                                                                    className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                    value={inlineDownPaymentDate}
                                                                    onChange={(e) => setInlineDownPaymentDate(e.target.value)}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs text-muted mb-1">Notizen (optional)</label>
                                                                <textarea 
                                                                    className="w-full px-3 py-2 bg-surface-hover border border-gray-500 rounded text-primary text-sm"
                                                                    rows={2}
                                                                    placeholder="Zusätzliche Notizen..."
                                                                    value={inlineDownPaymentNotes}
                                                                    onChange={(e) => setInlineDownPaymentNotes(e.target.value)}
                                                                />
                                                            </div>
                                                            <button 
                                                                type="button"
                                                                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white text-sm rounded font-medium transition"
                                                                disabled={isCreatingInlineDownPayment || !inlineDownPaymentValue || parseFloat(inlineDownPaymentValue) <= 0}
                                                                onClick={async () => {
                                                                    const result = await handleInlineDownPaymentCreation('xrechnung');
                                                                    if (!result.success) {
                                                                        alert(`Fehler: ${result.error}`);
                                                                    }
                                                                }}
                                                            >
                                                                {isCreatingInlineDownPayment ? (
                                                                    <>
                                                                        <svg className="animate-spin inline w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        Erstelle...
                                                                    </>
                                                                ) : (
                                                                    'Abschlagsrechnung erstellen (XRechnung)'
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Form Sections */}
                            <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('details.title')}</h2>
                                <InvoiceMetaForm 
                                    invoiceDate={invoiceDate}
                                    setInvoiceDate={setInvoiceDate}
                                    currency={currency}
                                    setCurrency={setCurrency}
                                    deliveryDate={deliveryDate}
                                    setDeliveryDate={setDeliveryDate}
                                    invoiceNumberPreview={xrechnungPreview?.preview}
                                    isLoadingPreview={isLoadingPreview}
                                    showTitle={false}
                                />
                            </div>
                            
                            <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('buyer.title')}</h2>
                                {/* XRechnung-spezifischer Hinweis */}
                                <div className="mb-4 p-3 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded">
                                    <p className="text-sm text-[var(--tone-warning-fg)]">
                                                                            <strong>{t('xrechnung.noteTitle')}</strong> {t('xrechnung.leitwegIdNote')}
                                    </p>
                                </div>
                                <BuyerDetailsForm 
                                    buyer={buyer}
                                    setBuyer={setBuyer}
                                    onCustomerSelect={handleCustomerSelection}
                                    requiresLeitwegId={true} // Neuer Prop für XRechnung
                                    showTitle={false}
                                />
                            </div>
                            
                            <div className="p-6 bg-surface-raised rounded-lg border border-hairline shadow-inner mb-6">
                                <h2 className="text-xl font-semibold mb-4 border-b border-strong pb-2 text-primary">{t('positions.title')}</h2>
                                <InvoiceItemsForm 
                                    items={items}
                                    setItems={setItems}
                                    currency={currency}
                                    showTitle={false}
                                />
                            </div>

                            {/* Optional Fields Section */} 
                            <CollapsibleSection
                                title={t('advanced.title')}
                                defaultExpanded={isAdvancedMode}
                                error={
                                    billingPeriodError
                                        ? (billingPeriodError === 'incomplete'
                                            ? t('advanced.periodIncompleteError')
                                            : t('advanced.periodOrderError'))
                                        : undefined
                                }
                            >
                                {/* ShipTo Address Fields */} 
                                <div className="mb-6">
                                    <h3 className="text-lg font-medium mb-4 text-accent-ink">{t('shipTo.title')}</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label htmlFor="shipToName" className="block text-sm font-medium text-secondary mb-1">{t('shipTo.nameLabel')}</label>
                                            <input type="text" id="shipToName" name="name" value={shipToAddress.name} onChange={(e) => setShipToAddress({...shipToAddress, name: e.target.value})} className="mt-1 block w-full px-3 py-2 bg-surface-inset border border-strong rounded-md shadow-sm text-primary placeholder:text-muted focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={t('shipTo.namePlaceholder')} />
                                        </div>
                                        {/* Weitere shipTo Felder... */}
                                    </div>
                                </div>
                                {/* Other Optional Fields Component */} 
                                <OptionalFieldsForm 
                                    paymentTerms={paymentTerms}
                                    setPaymentTerms={setPaymentTerms}
                                    notes={notes}
                                    setNotes={setNotes}
                                    billingStartDate={billingStartDate}
                                    setBillingStartDate={setBillingStartDate}
                                    billingEndDate={billingEndDate}
                                    setBillingEndDate={setBillingEndDate}
                                    tenderOrLotReference={tenderOrLotReference}
                                    setTenderOrLotReference={setTenderOrLotReference}
                                    invoicedObjectId={invoicedObjectId}
                                    setInvoicedObjectId={setInvoicedObjectId}
                                    invoicedObjectScheme={invoicedObjectScheme}
                                    setInvoicedObjectScheme={setInvoicedObjectScheme}
                                    contractReference={contractReference}
                                    setContractReference={setContractReference}
                                    projectReferenceId={projectReferenceId}
                                    setProjectReferenceId={setProjectReferenceId}
                                    projectReferenceName={projectReferenceName}
                                    setProjectReferenceName={setProjectReferenceName}
                                    paymentDueDate={paymentDueDate}
                                    setPaymentDueDate={setPaymentDueDate}
                                    paymentMandateReference={paymentMandateReference}
                                    setPaymentMandateReference={setPaymentMandateReference}
                                    buyerOrderReference={buyerOrderReference}
                                    setBuyerOrderReference={setBuyerOrderReference}
                                    sellerOrderReference={sellerOrderReference}
                                    setSellerOrderReference={setSellerOrderReference}
                                />
                            </CollapsibleSection>

                            {/* Action Buttons — M11b-3: für advisor (read-only) ausgeblendet */}
                            {!isReadOnly && (
                            <div className="mt-8 flex justify-end space-x-4">
                                {/* Draft Save Button */}
                                <button
                                    type="button"
                                    onClick={handleSaveDraft}
                                    disabled={isSavingDraft || !token}
                                    className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-md font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSavingDraft ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Speichert...
                                        </>
                                    ) : (
                                                                                    t('actions.saveAsDraft')
                                    )}
                                </button>

                                {/* Preview Generate Button */}
                                <button
                                    type="button"
                                    onClick={handleGeneratePreview}
                                    disabled={isGeneratingPreview || !token}
                                    className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-md font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isGeneratingPreview ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Generiert...
                                        </>
                                    ) : (
                                                                                    t('actions.generatePreview')
                                    )}
                                </button>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-md font-medium rounded-md text-[#0C1322] bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Bearbeitung...
                                        </>
                                    ) : (
                                                                                    hasDownPayments ? t('actions.createFinalInvoice') : t('actions.createXRechnung')
                                    )}
                                </button>
                            </div>
                            )}

                            {/* Erfolgs-Nachricht für Entwurf-Speicherung - in separater Zeile */}
                            {draftSaveSuccess && (
                                <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center">
                                    <svg className="flex-shrink-0 mr-2 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    {draftSaveSuccess}
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Customer Dialog */}
            <ConfirmationDialog 
                isOpen={customerDialogOpen}
                isProcessing={isSubmitting}
                title={customerDialogConfig.title}
                message={customerDialogConfig.message}
                options={customerDialogConfig.options || []}
                onClose={() => {
                    console.log("Kundendialog wird geschlossen");
                    setCustomerDialogOpen(false);
                }}
                dataComparison={customerDialogConfig.action === 'update' ? {
                    original: customerDialogConfig.original || {},
                    modified: customerDialogConfig.data,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'companyName', label: 'Firmenname' },
                        { key: 'personName', label: 'Ansprechpartner' },
                        { key: 'street', label: 'Straße' },
                        { key: 'zip', label: 'PLZ' },
                        { key: 'city', label: 'Ort' },
                        { key: 'country', label: 'Land' },
                        { key: 'email', label: 'E-Mail' },
                        { key: 'phone', label: 'Telefon' },
                        { key: 'website', label: 'Webseite' }
                    ]
                } : undefined}
            />

            {/* Product Dialog */}
            <ConfirmationDialog 
                isOpen={productDialogOpen}
                isProcessing={isSubmitting}
                title={productDialogConfig.title}
                message={productDialogConfig.message}
                options={[
                    { 
                        label: 'Änderungen speichern', 
                        action: () => onProductDialogAction('process'),
                        variant: 'primary' 
                    },
                    { 
                        label: 'Abbrechen', 
                        action: () => onProductDialogAction('cancel'),
                        variant: 'secondary'
                    }
                ]}
                onClose={() => {
                    console.log("Produktdialog wird geschlossen");
                    setProductDialogOpen(false);
                }}
                dataComparison={productDialogConfig.itemsToProcess.length === 1 && productDialogConfig.itemsToProcess[0].action === 'update' ? {
                    original: productDialogConfig.itemsToProcess[0].originalProduct || {},
                    modified: productDialogConfig.itemsToProcess[0].item,
                    displayFields: [
                        { key: 'name', label: 'Name' },
                        { key: 'description', label: 'Beschreibung' },
                        { key: 'netPrice', label: 'Netto-Preis' },
                        { key: 'taxRate', label: 'Steuersatz' },
                        { key: 'unitCode', label: 'Einheit' },
                        { key: 'sellerArticleId', label: 'Artikelnummer' },
                        { key: 'buyerArticleId', label: 'Kunden-Artikelnummer' }
                    ]
                } : undefined}
            />

            {/* Down Payment Modal */}
            {showDownPaymentModal && (
                <DownPaymentModal
                    isOpen={showDownPaymentModal}
                    onClose={closeDownPaymentModal}
                    baseDocument={{
                        id: 'current-xrechnung-draft',
                        type: 'invoice' as const,
                        totalAmount: items.reduce((sum, item) => sum + (item.netPrice * item.quantity), 0),
                        currency: currency,
                        documentNumber: xrechnungPreview?.preview || 'Entwurf'
                    }}
                    existingDownPayments={existingDownPayments}
                    onDownPaymentCreated={handleDownPaymentCreated}
                    format="xrechnung"
                />
            )}
        </div>
    );
};

export default XRechnungDashboard; 